import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import Logo from '../assets/sanjivni ayurveda logo.png'
import api from "../api/axios";
import f1 from '../assets/01.jpeg'
import f2 from '../assets/02.jpeg'
import f3 from '../assets/03.jpeg'
import f4 from '../assets/04.jpeg'
import f5 from '../assets/05.jpeg'
import f6 from '../assets/06.jpeg'

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */
const products = [
  { id: 1, emoji: f1, name: "Triphala Churna", category: "Digestive Health", price: "₹349", rating: 4.8, reviews: 214, tag: "Best Seller" },
  { id: 2, emoji: f2, name: "Ashwagandha Gold", category: "Stress & Immunity", price: "₹499", rating: 4.9, reviews: 389, tag: "New" },
  { id: 3, emoji: f3, name: "Tulsi Honey Blend", category: "Respiratory Wellness", price: "₹279", rating: 4.7, reviews: 156, tag: "Popular" },
  { id: 4, emoji: f4, name: "Brahmi Hair Oil", category: "Hair & Scalp Care", price: "₹399", rating: 4.6, reviews: 98, tag: "Trending" },
  { id: 5, emoji: f5, name: "Triphala Churna", category: "Digestive Health", price: "₹349", rating: 4.8, reviews: 214, tag: "Best Seller" },
  { id: 6, emoji: f6, name: "Ashwagandha Gold", category: "Stress & Immunity", price: "₹499", rating: 4.9, reviews: 389, tag: "New" },
];

const benefits = [
  { icon: "🌱", title: "100% Natural Ingredients", desc: "Every formulation is crafted from sustainably sourced herbs, free from synthetic additives, preservatives, or artificial colours." },
  { icon: "⚖️", title: "Balances Mind & Body", desc: "Rooted in the tridosha philosophy, our blends restore harmony between Vata, Pitta, and Kapha for whole-person wellness." },
  { icon: "🔬", title: "Clinically Validated", desc: "All products undergo rigorous third-party lab testing and are GMP-certified, ensuring potency, purity, and safety." },
  { icon: "♻️", title: "Eco-Conscious Packaging", desc: "We use compostable and recycled materials, because caring for your health should never come at the planet's expense." },
];

/* ─────────────────────────────────────────────
   STAR RATING
───────────────────────────────────────────── */
const Stars = ({ rating }) => (
  <span className="stars">
    {[1, 2, 3, 4, 5].map(s => (
      <span key={s} style={{ color: s <= Math.round(rating) ? "#d4a017" : "#ccc" }}>★</span>
    ))}
  </span>
);

/* ─────────────────────────────────────────────
   SECTION REVEAL HOOK
───────────────────────────────────────────── */
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.12 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

/* ─────────────────────────────────────────────
   BANNER SLIDER COMPONENT
───────────────────────────────────────────── */
function BannerSlider({ banners }) {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  const next = useCallback(() => setCurrent(c => (c + 1) % banners.length), [banners.length]);
  const prev = useCallback(() => setCurrent(c => (c - 1 + banners.length) % banners.length), [banners.length]);

  useEffect(() => {
    if (banners.length <= 1 || paused) return;
    timerRef.current = setInterval(next, 4000);
    return () => clearInterval(timerRef.current);
  }, [banners.length, paused, next]);

  if (!banners.length) return null;

  return (
    <div
      className="banner-slider"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Slides */}
      <div className="banner-slider__track">
        {banners.map((banner, i) => (
          <div
            key={banner._id}
            className={`banner-slide ${i === current ? "banner-slide--active" : i === (current - 1 + banners.length) % banners.length ? "banner-slide--prev" : ""}`}
          >
            {/* Background image */}
            {banner.image?.url && (
              <img
                src={banner.image.url}
                alt={banner.title || "Banner"}
                className="banner-slide__img"
              />
            )}
            {/* Overlay */}
            <div className="banner-slide__overlay" />

            {/* Content */}
            {(banner.title || banner.subtitle || banner.link) && (
              <div className="banner-slide__content">
                {banner.title && <h2 className="banner-slide__title">{banner.title}</h2>}
                {banner.subtitle && <p className="banner-slide__sub">{banner.subtitle}</p>}
                {banner.link && (
                  <Link
                    to={banner.link.startsWith("http") ? banner.link : banner.link}
                    className="banner-slide__btn"
                    onClick={e => banner.link.startsWith("http") && e.preventDefault() && window.open(banner.link)}
                  >
                    Shop Now →
                  </Link>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Arrows — only if more than 1 banner */}
      {banners.length > 1 && (
        <>
          <button className="banner-arrow banner-arrow--prev" onClick={prev} aria-label="Previous banner">‹</button>
          <button className="banner-arrow banner-arrow--next" onClick={next} aria-label="Next banner">›</button>

          {/* Dots */}
          <div className="banner-dots">
            {banners.map((_, i) => (
              <button
                key={i}
                className={`banner-dot ${i === current ? "banner-dot--active" : ""}`}
                onClick={() => { setCurrent(i); setPaused(true); setTimeout(() => setPaused(false), 5000); }}
                aria-label={`Go to banner ${i + 1}`}
              />
            ))}
          </div>

          {/* Progress bar */}
          <div className="banner-progress">
            <div
              key={current}
              className="banner-progress__fill"
              style={{ animationDuration: paused ? "0s" : "4s" }}
            />
          </div>
        </>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   HOME PAGE
───────────────────────────────────────────── */
export default function Home() {
  const [heroRef, heroVis] = useReveal();
  const [prodRef, prodVis] = useReveal();
  const [benRef, benVis] = useReveal();
  const [testRef, testVis] = useReveal();

  const [banners, setBanners] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const navigate = useNavigate();

  /* ── Fetch banners ── */
  useEffect(() => {
    api.get("/admin/settings")
      .then(({ data }) => {
        const all = data?.data?.settings?.banners || [];
        const active = all
          .filter(b => b.isActive !== false)
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        setBanners(active);
      })
      .catch(() => { }); // fail silently — site still works without banners
  }, []);

  /* ── Fetch testimonials ── */
  useEffect(() => {
    api.get("/reviews")
      .then(({ data }) => {
        const formatted = data.data.map(r => ({
          id: r._id,
          name: r.username,
          quote: r.comment,
          location: r.location || "India",
          product: r.productId?.name || "",
          rating: r.rating,
          avatar: r.username?.charAt(0)?.toUpperCase(),
        }));
        setTestimonials(formatted);
      })
      .catch(() => { });
  }, []);

  /* ── Auto-advance testimonials ── */
  useEffect(() => {
    if (testimonials.length === 0) return;
    const t = setInterval(() => {
      setActiveTestimonial(p => p === testimonials.length - 1 ? 0 : p + 1);
    }, 4500);
    return () => clearInterval(t);
  }, [testimonials]);

  useEffect(() => {
    if (testimonials.length > 0) setActiveTestimonial(0);
  }, [testimonials]);

  const MAX_DOTS = 5;
  const getVisibleDots = () => {
    const total = testimonials.length;
    if (total <= MAX_DOTS) return testimonials;
    let start = Math.max(0, activeTestimonial - 2);
    let end = start + MAX_DOTS;
    if (end > total) { end = total; start = end - MAX_DOTS; }
    return testimonials.slice(start, end);
  };

  return (
    <>
      <style>{css}</style>


      {/* ── HERO ── */}
      <section className={`hero ${heroVis ? "revealed" : ""}`} ref={heroRef}>
        <div className="hero__bg-pattern" aria-hidden="true" />
        <div className="hero__orb hero__orb--1" aria-hidden="true" />
        <div className="hero__orb hero__orb--2" aria-hidden="true" />

        <div className="hero__content">
          <span className="hero__eyebrow">🌿 Rooted in Tradition · Crafted for Life</span>
          <h1 className="hero__heading">
            Heal Naturally.<br />
            <em>Live Vibrantly.</em>
          </h1>
          <p className="hero__sub">
            Discover Ayurvedic formulations passed down through generations —
            now rigorously tested, beautifully crafted, and delivered to your door.
          </p>
          <div className="hero__ctas">
            <button className="btn btn--primary"><Link to='/products'>Shop the Collection →</Link></button>
            <button className="btn btn--ghost"><Link to='/about'>Learn Our Story</Link></button>
          </div>
          <div className="hero__stats">
            {[["50K+", "Happy Customers"], ["120+", "Herbal Products"], ["5000", "Years of Wisdom"]].map(([num, label]) => (
              <div className="hero__stat" key={label}>
                <strong>{num}</strong>
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="hero__visual">
          <div className="hero__circle">
            <div className="hero__circle-inner">
              <span className="hero__plant"><img src={Logo} alt="Sanjivani" /></span>
              <span className="hero__plant-label">Sanjivani</span>
            </div>
            <div className="hero__orbit">
              {["🌸", "🍃", "🫙", "🌻"].map((e, i) => (
                <div className="hero__orbit-item" key={i} style={{ "--i": i }}>{e}</div>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* ── BANNER SLIDER (from admin panel) ── */}
      {/* {banners.length > 0 && <BannerSlider banners={banners} />} */}

      {/* ── PRODUCTS ── */}
      <section className={`section products-section ${prodVis ? "revealed" : ""}`} ref={prodRef}>
        <div className="container">
          <div className="section-header">
            <span className="section-eyebrow">✦ Featured Collection</span>
            <h2 className="section-title">Our Finest Formulations</h2>
            <p className="section-sub">Each product is a confluence of ancient recipes and modern science.</p>
          </div>
          <div className="products-grid">
            {products.map((p, i) => (
              <article className="product-card" key={p.id} style={{ "--delay": `${i * 0.1}s` }} onClick={() => navigate('/products')}>
                <div className="product-card__emoji"><img src={p.emoji} alt={p.name} /></div>
              </article>
            ))}
          </div>
          <div className="products-cta">
            <button className="btn btn--outline" onClick={() => navigate('/products')}>View All Products →</button>
          </div>
        </div>
      </section>

      {/* ── BENEFITS ── */}
      <section className={`section benefits-section ${benVis ? "revealed" : ""}`} ref={benRef}>
        <div className="container">
          <div className="section-header">
            <span className="section-eyebrow">✦ Why Ayurveda</span>
            <h2 className="section-title">The Sanjivani Difference</h2>
          </div>
          <div className="benefits-grid">
            {benefits.map((b, i) => (
              <div className="benefit-card" key={b.title} style={{ "--delay": `${i * 0.12}s` }}>
                <div className="benefit-card__icon">{b.icon}</div>
                <h3 className="benefit-card__title">{b.title}</h3>
                <p className="benefit-card__desc">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section className={`section testimonials-section ${testVis ? "revealed" : ""}`} ref={testRef}>
        <div className="testimonials-bg" aria-hidden="true" />
        <div className="container">
          <div className="section-header section-header--light">
            <span className="section-eyebrow section-eyebrow--gold">✦ Customer Love</span>
            <h2 className="section-title section-title--light">Words From Our Community</h2>
          </div>
          <div className="testimonials-carousel">
            {testimonials.map((t, i) => (
              <div key={t.id} className={`testimonial-card ${i === activeTestimonial ? "testimonial-card--active" : ""}`}>
                <div className="testimonial-card__quote">"{t.quote}"</div>
                <div className="testimonial-card__footer">
                  <div className="testimonial-card__avatar">{t.avatar}</div>
                  <div>
                    <strong className="testimonial-card__name">{t.name}</strong>
                    <span className="testimonial-card__location">📍 {t.location}</span>
                    <div className="testimonial-card__product">Purchased: {t.product}</div>
                  </div>
                  <Stars rating={Number(t.rating)} />
                </div>
              </div>
            ))}
          </div>
          <div className="testimonial-dots">
            {getVisibleDots().map((_, index) => {
              const realIndex = testimonials.length <= MAX_DOTS
                ? index
                : Math.max(0, activeTestimonial - 2) + index;
              return (
                <button
                  key={realIndex}
                  className={`testimonial-dot ${realIndex === activeTestimonial ? "testimonial-dot--active" : ""}`}
                  onClick={() => setActiveTestimonial(realIndex)}
                />
              );
            })}
          </div>
        </div>
      </section>

      {/* ── BANNER CTA ── */}
      <section className="banner-cta">
        <div className="container banner-cta__inner">
          <div>
            <h2 className="banner-cta__heading">Begin Your Wellness Journey</h2>
            <p className="banner-cta__sub">Free shipping on orders above ₹499 · Across India</p>
          </div>
          <button className="btn btn--primary btn--lg"><Link to='/products'>Shop Now →</Link></button>
        </div>
      </section>
    </>
  );
}

/* ─────────────────────────────────────────────
   CSS
───────────────────────────────────────────── */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,400;0,600;1,400&family=Jost:wght@300;400;500;600&display=swap');

:root {
  --c-primary:#2d6a4f;--c-primary-light:#40916c;--c-primary-dark:#1b4332;
  --c-accent:#d4a017;--c-accent-soft:#fef3c7;--c-bg:#f9f6f0;--c-bg-dark:#1b2a1e;
  --c-surface:#ffffff;--c-text:#1c2b22;--c-muted:#5a7262;--c-border:#d4e6d5;
  --f-display:'Cormorant Garamond',Georgia,serif;--f-body:'Jost',sans-serif;
  --radius:16px;--radius-sm:8px;
  --shadow:0 4px 24px rgba(27,67,50,.10);--shadow-lg:0 12px 48px rgba(27,67,50,.16);
  --ease:cubic-bezier(.4,0,.2,1);
}
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
body{font-family:var(--f-body);background:var(--c-bg);color:var(--c-text);}
a{text-decoration:none;color:inherit;}
.container{max-width:1160px;margin:0 auto;padding:0 24px;}

/* ── BUTTONS ── */
.btn{display:inline-flex;align-items:center;gap:8px;font-family:var(--f-body);font-size:.85rem;font-weight:600;letter-spacing:.06em;text-transform:uppercase;padding:13px 28px;border-radius:100px;border:none;cursor:pointer;transition:transform .22s var(--ease),box-shadow .22s var(--ease),background .22s;}
.btn--primary{background:var(--c-primary);color:#fff;box-shadow:0 4px 16px rgba(45,106,79,.30);}
.btn--primary:hover{background:var(--c-primary-dark);transform:translateY(-2px);box-shadow:0 8px 28px rgba(45,106,79,.38);}
.btn--ghost{background:transparent;color:var(--c-primary-dark);border:1.5px solid var(--c-border);}
.btn--ghost:hover{background:rgba(45,106,79,.06);transform:translateY(-1px);}
.btn--outline{background:transparent;color:var(--c-primary);border:1.5px solid var(--c-primary);}
.btn--outline:hover{background:var(--c-primary);color:#fff;transform:translateY(-1px);}
.btn--lg{padding:16px 36px;font-size:.9rem;}
.stars{font-size:.9rem;letter-spacing:2px;}

/* ── SECTION REVEAL ── */
.section,.hero{opacity:0;transform:translateY(36px);transition:opacity .7s var(--ease),transform .7s var(--ease);}
.section.revealed,.hero.revealed{opacity:1;transform:none;}
.section{padding:96px 0;}
.section-header{text-align:center;margin-bottom:56px;}
.section-eyebrow{display:inline-block;font-size:.72rem;font-weight:600;letter-spacing:.18em;text-transform:uppercase;color:var(--c-primary-light);background:rgba(45,106,79,.09);padding:5px 16px;border-radius:100px;margin-bottom:16px;}
.section-eyebrow--gold{color:var(--c-accent);background:rgba(212,160,23,.12);}
.section-title{font-family:var(--f-display);font-size:clamp(2rem,4vw,3rem);font-weight:600;color:var(--c-primary-dark);line-height:1.2;margin-bottom:14px;}
.section-title--light{color:#fff;}
.section-sub{font-size:1.05rem;color:var(--c-muted);font-weight:300;max-width:520px;margin:0 auto;}
.section-header--light .section-sub{color:rgba(255,255,255,.65);}

/* ══════════════════════════════
   BANNER SLIDER
══════════════════════════════ */
.banner-slider{
  position:relative;width:100%;
  height:clamp(220px,45vw,520px);
  overflow:hidden;background:#1b2a1e;
}
.banner-slider__track{position:relative;width:100%;height:100%;}

.banner-slide{
  position:absolute;inset:0;
  opacity:0;
  transform:scale(1.04);
  transition:opacity .7s var(--ease),transform .7s var(--ease);
  pointer-events:none;
}
.banner-slide--active{
  opacity:1;transform:scale(1);pointer-events:auto;z-index:1;
}
.banner-slide--prev{opacity:0;transform:scale(.97);}

.banner-slide__img{
  width:100%;height:100%;object-fit:cover;display:block;
}
.banner-slide__overlay{
  position:absolute;inset:0;
  background:linear-gradient(90deg,rgba(27,67,50,.72) 0%,rgba(27,67,50,.3) 50%,transparent 100%);
}

.banner-slide__content{
  position:absolute;left:clamp(24px,6vw,80px);top:50%;
  transform:translateY(-50%);z-index:2;max-width:520px;
}
.banner-slide__title{
  font-family:var(--f-display);
  font-size:clamp(1.4rem,3.5vw,3rem);
  font-weight:600;color:#fff;
  margin-bottom:10px;line-height:1.2;
  text-shadow:0 2px 12px rgba(0,0,0,.3);
}
.banner-slide__sub{
  font-size:clamp(.82rem,1.5vw,1.1rem);
  color:rgba(255,255,255,.82);
  font-weight:300;margin-bottom:22px;
  line-height:1.5;
}
.banner-slide__btn{
  display:inline-flex;align-items:center;gap:8px;
  font-family:var(--f-body);font-size:.82rem;font-weight:700;
  letter-spacing:.08em;text-transform:uppercase;
  padding:12px 28px;border-radius:100px;
  background:#fff;color:var(--c-primary-dark);
  border:none;cursor:pointer;text-decoration:none;
  box-shadow:0 4px 16px rgba(0,0,0,.2);
  transition:background .22s,transform .22s,box-shadow .22s;
}
.banner-slide__btn:hover{background:var(--c-accent-soft);transform:translateY(-2px);box-shadow:0 8px 24px rgba(0,0,0,.25);}

/* Arrows */
.banner-arrow{
  position:absolute;top:50%;transform:translateY(-50%);
  z-index:10;width:44px;height:44px;border-radius:50%;
  background:rgba(255,255,255,.15);backdrop-filter:blur(6px);
  border:1px solid rgba(255,255,255,.25);color:#fff;
  font-size:1.5rem;cursor:pointer;
  display:flex;align-items:center;justify-content:center;
  transition:background .22s,transform .22s;
  line-height:1;
}
.banner-arrow:hover{background:rgba(255,255,255,.28);transform:translateY(-50%) scale(1.08);}
.banner-arrow--prev{left:18px;}
.banner-arrow--next{right:18px;}

/* Dots */
.banner-dots{
  position:absolute;bottom:18px;left:50%;transform:translateX(-50%);
  display:flex;gap:8px;z-index:10;
}
.banner-dot{
  width:8px;height:8px;border-radius:4px;
  background:rgba(255,255,255,.35);border:none;cursor:pointer;
  transition:background .3s,width .3s;
}
.banner-dot--active{background:#fff;width:24px;}

/* Progress bar */
.banner-progress{
  position:absolute;bottom:0;left:0;right:0;height:3px;
  background:rgba(255,255,255,.15);z-index:10;
}
.banner-progress__fill{
  height:100%;background:var(--c-accent);
  animation:bannerProgress linear forwards;
  transform-origin:left;
}
@keyframes bannerProgress{from{width:0}to{width:100%}}

/* ══════════════════════════════
   HERO
══════════════════════════════ */
.hero{min-height:100svh;position:relative;overflow:hidden;display:grid;grid-template-columns:1fr 1fr;align-items:center;gap:48px;padding:120px 190px 80px;margin:0 auto;}
.hero__bg-pattern{position:fixed;inset:0;z-index:-2;pointer-events:none;background:radial-gradient(ellipse 70% 60% at 70% 40%,rgba(64,145,108,.08) 0%,transparent 70%),radial-gradient(ellipse 50% 50% at 20% 80%,rgba(212,160,23,.05) 0%,transparent 60%);}
.hero__orb{position:absolute;border-radius:50%;filter:blur(60px);pointer-events:none;z-index:-1;}
.hero__orb--1{width:420px;height:420px;right:-80px;top:-80px;background:radial-gradient(circle,rgba(64,145,108,.18),transparent 70%);animation:float 8s ease-in-out infinite;}
.hero__orb--2{width:300px;height:300px;left:-60px;bottom:40px;background:radial-gradient(circle,rgba(212,160,23,.12),transparent 70%);animation:float 11s ease-in-out infinite reverse;}
@keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-24px)}}
.hero__eyebrow{display:inline-block;font-size:.78rem;font-weight:500;letter-spacing:.12em;text-transform:uppercase;color:var(--c-primary-light);background:rgba(45,106,79,.08);padding:7px 18px;border-radius:100px;margin-bottom:24px;border:1px solid var(--c-border);}
.hero__heading{font-family:var(--f-display);font-size:clamp(3rem,5.5vw,5rem);font-weight:600;line-height:1.1;color:var(--c-primary-dark);margin-bottom:20px;}
.hero__heading em{font-style:italic;color:var(--c-primary-light);display:block;}
.hero__sub{font-size:1.08rem;font-weight:300;color:var(--c-muted);line-height:1.7;max-width:460px;margin-bottom:36px;}
.hero__ctas{display:flex;gap:16px;flex-wrap:wrap;margin-bottom:48px;}
.hero__stats{display:flex;gap:36px;flex-wrap:wrap;}
.hero__stat{display:flex;flex-direction:column;}
.hero__stat strong{font-size:2rem;font-weight:600;color:var(--c-primary-dark);line-height:1;}
.hero__stat span{font-size:.78rem;font-weight:500;color:var(--c-muted);letter-spacing:.05em;margin-top:4px;}
.hero__visual{display:flex;align-items:center;justify-content:center;}
.hero__circle{position:relative;width:340px;height:340px;display:flex;align-items:center;justify-content:center;}
.hero__circle-inner{width:180px;height:180px;border-radius:50%;background:linear-gradient(135deg,var(--c-primary),var(--c-primary-light));display:flex;flex-direction:column;align-items:center;justify-content:center;box-shadow:0 20px 60px rgba(45,106,79,.35);z-index:2;}
.hero__plant{font-size:4rem;line-height:1;}
.hero__plant img{width:150px;object-fit:contain;border-radius:50%;}
.hero__plant-label{font-family:var(--f-display);font-size:.9rem;font-style:italic;color:rgba(255,255,255,.85);margin-top:4px;}
.hero__orbit{position:absolute;inset:0;animation:spin 20s linear infinite;}
@keyframes spin{to{transform:rotate(360deg)}}
.hero__orbit-item{position:absolute;width:52px;height:52px;background:var(--c-surface);border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:1.4rem;box-shadow:var(--shadow);border:1px solid var(--c-border);top:50%;left:50%;transform:translate(-50%,-50%) rotate(calc(var(--i)*90deg)) translateY(-160px) rotate(calc(var(--i)*-90deg));animation:counter-spin 20s linear infinite;}
@keyframes counter-spin{to{transform:translate(-50%,-50%) rotate(calc(var(--i)*90deg + 360deg)) translateY(-160px) rotate(calc(-1*(var(--i)*90deg + 360deg)));}}

/* ══════════════════════════════
   PRODUCTS
══════════════════════════════ */
.products-section{background:var(--c-surface);}
.products-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:24px;margin-bottom:48px;}
.product-card{background:var(--c-bg);border:1px solid var(--c-border);border-radius:var(--radius);padding:28px 22px 22px;display:flex;flex-direction:column;position:relative;overflow:hidden;transition:transform .28s var(--ease),box-shadow .28s var(--ease),border-color .28s;animation:fadeUp .6s var(--ease) both;animation-delay:var(--delay,0s);cursor:pointer;}
@keyframes fadeUp{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:none}}
.product-card:hover{transform:translateY(-6px);box-shadow:var(--shadow-lg);border-color:var(--c-primary-light);}
.product-card::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:linear-gradient(90deg,var(--c-primary),var(--c-primary-light));opacity:0;transition:opacity .28s;}
.product-card:hover::before{opacity:1;}
.product-card__emoji{font-size:3rem;margin-bottom:18px;line-height:1;transition:transform .28s var(--ease);}
.product-card__emoji img{width:100%;border-radius:12px;object-fit:cover;}
.product-card:hover .product-card__emoji{transform:scale(1.04);}
.products-cta{text-align:center;}

/* ══════════════════════════════
   BENEFITS
══════════════════════════════ */
.benefits-section{background:linear-gradient(160deg,rgba(45,106,79,.04) 0%,var(--c-bg) 50%,rgba(212,160,23,.04) 100%);}
.benefits-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:24px;}
.benefit-card{background:var(--c-surface);border:1px solid var(--c-border);border-radius:var(--radius);padding:36px 28px;text-align:center;transition:transform .28s var(--ease),box-shadow .28s var(--ease);position:relative;overflow:hidden;animation:fadeUp .6s var(--ease) both;animation-delay:var(--delay,0s);}
.benefit-card::after{content:'';position:absolute;bottom:0;left:0;right:0;height:3px;background:linear-gradient(90deg,var(--c-primary-light),var(--c-accent));opacity:0;transition:opacity .28s;}
.benefit-card:hover{transform:translateY(-6px);box-shadow:var(--shadow-lg);}
.benefit-card:hover::after{opacity:1;}
.benefit-card__icon{font-size:2.8rem;margin-bottom:20px;display:block;filter:drop-shadow(0 4px 8px rgba(45,106,79,.2));transition:transform .28s var(--ease);}
.benefit-card:hover .benefit-card__icon{transform:scale(1.15) rotate(-5deg);}
.benefit-card__title{font-size:1rem;font-weight:600;color:var(--c-primary-dark);margin-bottom:12px;}
.benefit-card__desc{font-size:.88rem;color:var(--c-muted);font-weight:300;line-height:1.7;}

/* ══════════════════════════════
   TESTIMONIALS
══════════════════════════════ */
.testimonials-section{position:relative;padding:96px 0;overflow:hidden;}
.testimonials-bg{position:absolute;inset:0;background:linear-gradient(135deg,var(--c-primary-dark) 0%,#1b3a2a 50%,#0f2016 100%);z-index:-1;}
.testimonials-bg::after{content:'';position:absolute;inset:0;background-image:radial-gradient(circle at 20% 50%,rgba(64,145,108,.18) 0%,transparent 50%),radial-gradient(circle at 80% 30%,rgba(212,160,23,.1) 0%,transparent 40%);}
.testimonials-carousel{position:relative;min-height:200px;}
.testimonial-card{position:absolute;inset:0;background:rgba(255,255,255,.06);backdrop-filter:blur(8px);border:1px solid rgba(255,255,255,.12);border-radius:var(--radius);padding:40px 44px;opacity:0;transform:translateY(16px) scale(.98);transition:opacity .5s var(--ease),transform .5s var(--ease);pointer-events:none;}
.testimonial-card--active{opacity:1;transform:none;pointer-events:auto;position:relative;}
.testimonial-card__quote{font-family:var(--f-display);font-size:clamp(1.1rem,2vw,1.45rem);font-style:italic;color:rgba(255,255,255,.92);line-height:1.6;margin-bottom:28px;}
.testimonial-card__footer{display:flex;align-items:center;gap:16px;flex-wrap:wrap;}
.testimonial-card__avatar{width:48px;height:48px;border-radius:50%;background:linear-gradient(135deg,var(--c-primary-light),var(--c-primary));color:#fff;font-size:.85rem;font-weight:700;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
.testimonial-card__name{display:block;color:#fff;font-weight:600;font-size:1rem;}
.testimonial-card__location{display:block;font-size:.8rem;color:rgba(255,255,255,.5);margin-top:2px;}
.testimonial-card__product{font-size:.72rem;color:var(--c-accent);margin-top:4px;font-weight:500;letter-spacing:.06em;}
.testimonial-dots{display:flex;justify-content:center;gap:10px;margin-top:36px;}
.testimonial-dot{width:10px;height:10px;border-radius:5px;background:rgba(255,255,255,.25);border:none;cursor:pointer;transition:background .3s,width .3s;}
.testimonial-dot--active{background:var(--c-accent);width:28px;}

/* ══════════════════════════════
   BANNER CTA
══════════════════════════════ */
.banner-cta{background:linear-gradient(90deg,var(--c-primary) 0%,var(--c-primary-light) 100%);padding:60px 24px;}
.banner-cta__inner{display:flex;align-items:center;justify-content:space-between;gap:32px;flex-wrap:wrap;}
.banner-cta__heading{font-family:var(--f-display);font-size:clamp(1.8rem,3vw,2.5rem);font-weight:600;color:#fff;margin-bottom:8px;}
.banner-cta__sub{color:rgba(255,255,255,.75);font-size:.95rem;font-weight:300;}
.banner-cta .btn--primary{background:#fff;color:var(--c-primary-dark);flex-shrink:0;box-shadow:0 4px 20px rgba(0,0,0,.15);}
.banner-cta .btn--primary:hover{background:var(--c-accent-soft);transform:translateY(-2px);}

/* ══════════════════════════════
   RESPONSIVE
══════════════════════════════ */
@media(max-width:1024px){.products-grid{grid-template-columns:repeat(2,1fr);}.benefits-grid{grid-template-columns:repeat(2,1fr);}}
@media(max-width:768px){
  .hero{grid-template-columns:1fr;text-align:center;padding:100px 24px 60px;gap:56px;}
  .hero__sub{max-width:100%;margin-left:auto;margin-right:auto;}
  .hero__ctas{justify-content:center;}
  .hero__stats{justify-content:center;}
  .hero__visual{order:-1;}
  .hero__circle{width:260px;height:260px;}
  .hero__circle-inner{width:140px;height:140px;}
  .hero__orbit-item{transform:translate(-50%,-50%) rotate(calc(var(--i)*90deg)) translateY(-120px) rotate(calc(var(--i)*-90deg));}
  .section{padding:72px 0;}
  .testimonial-card{padding:28px 24px;}
  .banner-slide__content{left:20px;max-width:85%;}
  .banner-arrow{width:36px;height:36px;font-size:1.2rem;}
}
@media(max-width:560px){
  .products-grid{grid-template-columns:1fr;}
  .benefits-grid{grid-template-columns:1fr;}
  .hero__ctas{flex-direction:column;align-items:center;}
  .banner-cta__inner{flex-direction:column;text-align:center;}
  .banner-slider{height:clamp(180px,55vw,280px);}
  .banner-slide__title{font-size:1.2rem;}
  .banner-slide__sub{display:none;}
}
`;